// A string-keyed memo for a PURE date function, with a ceiling.
//
// Built for `weekStartOf` (lib/window.ts) and `mondayUtc`
// (lib/affiliate-campaigns.ts), which the dashboards call once per fact per
// column: the same few hundred calendar dates asked tens of thousands of times a
// render, each answer a regex, a Date and string padding.
//
// ⚠️ ONLY FOR A FUNCTION OF ITS ARGUMENT ALONE. Both of those are UTC
// calendar arithmetic on the string they are given: no clock, no zone, no store.
// So an answer is the same on the server and in the browser, today and next
// year, and the memo can live for the life of the module. A function that read
// `Date.now()` or the local zone would be wrong to memoise here.
//
// BOUNDED, because it lives in the client bundle too. The key space is the
// distinct dates a page asks about (a corpus spans a few hundred days); past
// `cap` the map is emptied and starts again, so a caller that streams junk
// keys costs a re-computation, never memory.

export function boundedMemo<T>(fn: (key: string) => T, cap = 20_000): ((key: string) => T) & { size: () => number } {
  const held = new Map<string, T>();
  const memo = (key: string): T => {
    const hit = held.get(key);
    if (hit !== undefined || held.has(key)) return hit as T;
    const value = fn(key);
    if (held.size >= cap) held.clear();
    held.set(key, value);
    return value;
  };
  return Object.assign(memo, { size: () => held.size });
}
